import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DefensePosition } from "../../shared/domain/defense";
import type { PlayerListItem } from "../shared/types/contracts";

interface PreferenceProjection {
  actionId: string;
  playerId: string;
  positionPreferences: DefensePosition[];
  committedPlayerRevision: number | null;
  acknowledgement: Promise<void>;
  resolveAcknowledgement: () => void;
}

interface PreferenceProjectionHandle {
  positionPreferences: DefensePosition[];
  acknowledgement: Promise<void>;
}

export interface OptimisticPlayerPreferencesController {
  players: PlayerListItem[];
  prefs: Record<string, string[]>;
  projectToggle: (
    actionId: string,
    playerId: string,
    position: DefensePosition,
  ) => PreferenceProjectionHandle | null;
  currentRevision: (playerId: string) => number;
  markCommitted: (actionId: string, playerRevision: number) => void;
  reject: (actionId: string) => void;
}

export function useOptimisticPlayerPreferences({
  scopeKey,
  players: authoritativePlayers,
}: {
  scopeKey: string;
  players: PlayerListItem[];
}): OptimisticPlayerPreferencesController {
  const [projections, setProjections] = useState<PreferenceProjection[]>([]);
  const projectionsRef = useRef(projections);
  const playersRef = useRef(authoritativePlayers);
  const scopeRef = useRef(scopeKey);
  playersRef.current = authoritativePlayers;
  const publish = useCallback((next: PreferenceProjection[]) => {
    projectionsRef.current = next;
    setProjections(next);
  }, []);

  useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    const pending = projectionsRef.current;
    publish([]);
    pending.forEach((projection) => projection.resolveAcknowledgement());
  }, [publish, scopeKey]);

  const reconcile = useCallback(() => {
    const players = playersRef.current;
    const remaining: PreferenceProjection[] = [];
    let changed = false;
    for (const projection of projectionsRef.current) {
      const player = players.find(
        (candidate) => candidate.id === projection.playerId,
      );
      if (
        !player ||
        (projection.committedPlayerRevision !== null &&
          player.revision >= projection.committedPlayerRevision)
      ) {
        projection.resolveAcknowledgement();
        changed = true;
      } else {
        remaining.push(projection);
      }
    }
    if (changed) {
      publish(remaining);
    }
  }, [publish]);

  useEffect(reconcile, [authoritativePlayers, reconcile]);

  const projectToggle = useCallback(
    (actionId: string, playerId: string, position: DefensePosition) => {
      const player = playersRef.current.find(
        (candidate) => candidate.id === playerId,
      );
      if (!player) return null;
      const latest = [...projectionsRef.current]
        .reverse()
        .find((projection) => projection.playerId === playerId);
      const current = latest?.positionPreferences ?? player.positionPreferences;
      const positionPreferences = current.includes(position)
        ? current.filter((candidate) => candidate !== position)
        : [...current, position];
      let resolveAcknowledgement!: () => void;
      const acknowledgement = new Promise<void>((resolve) => {
        resolveAcknowledgement = resolve;
      });
      publish([
        ...projectionsRef.current,
        {
          actionId,
          playerId,
          positionPreferences,
          committedPlayerRevision: null,
          acknowledgement,
          resolveAcknowledgement,
        },
      ]);
      return { positionPreferences, acknowledgement };
    },
    [publish],
  );

  const currentRevision = useCallback((playerId: string) => {
    const authoritative = playersRef.current.find(
      (candidate) => candidate.id === playerId,
    );
    return Math.max(
      authoritative?.revision ?? 0,
      ...projectionsRef.current
        .filter((projection) => projection.playerId === playerId)
        .map((projection) => projection.committedPlayerRevision ?? 0),
    );
  }, []);

  const markCommitted = useCallback(
    (actionId: string, playerRevision: number) => {
      const projection = projectionsRef.current.find(
        (candidate) => candidate.actionId === actionId,
      );
      if (!projection) return;
      projection.committedPlayerRevision = playerRevision;
      reconcile();
    },
    [reconcile],
  );

  const reject = useCallback(
    (actionId: string) => {
      const index = projectionsRef.current.findIndex(
        (projection) => projection.actionId === actionId,
      );
      if (index < 0) return;
      const playerId = projectionsRef.current[index]!.playerId;
      const pending = projectionsRef.current.filter(
        (projection, candidateIndex) =>
          candidateIndex >= index && projection.playerId === playerId,
      );
      publish(
        projectionsRef.current.filter(
          (projection, candidateIndex) =>
            candidateIndex < index || projection.playerId !== playerId,
        ),
      );
      pending.forEach((projection) => projection.resolveAcknowledgement());
    },
    [publish],
  );

  const players = useMemo(() => {
    const latestByPlayer = new Map(
      (scopeRef.current === scopeKey ? projections : []).map((projection) => [
        projection.playerId,
        projection.positionPreferences,
      ]),
    );
    return authoritativePlayers.map((player) => {
      const positionPreferences = latestByPlayer.get(player.id);
      return positionPreferences ? { ...player, positionPreferences } : player;
    });
  }, [authoritativePlayers, projections, scopeKey]);
  const prefs = useMemo(
    () =>
      Object.fromEntries(
        players.map((player) => [player.id, player.positionPreferences]),
      ),
    [players],
  );

  return {
    players,
    prefs,
    projectToggle,
    currentRevision,
    markCommitted,
    reject,
  };
}
